import { trackEvent } from "../utils/track-event";
import type { TrackingEvent, TrackingEventResponse } from "../utils/types";
import type { SingleProduct } from "src/types/graphql.d";
import type { LineItem } from "src/types/ctgraphql.d";
import { getCartItemsCount } from "src/utils/ct/cart-helper";

type ProductAddedEventProps = {
  product: SingleProduct;
  size: string;
  variationId?: string;
  cartData?: {
    product_count?: number;
    cart_total?: number;
  };
  lineItems?: LineItem[];
  email?: string | null;
  resultId?: string;
};

type ProductAddedAnalyticsProps = {
  brand: string;
  brand_id: string;
  category: unknown;
  category_id: unknown;
  currency: string;
  image_url?: string | null;
  labels: string[];
  name: string;
  price: number;
  product_discount?: number | null;
  product_id: string;
  product_kids_gender?: unknown;
  product_shop_type?: unknown;
  product_size: string;
  quantity: number;
  regular_price: number;
  sku?: string | null;
  url: string;
  variant: string;
  product_variation?: string | null;
  gender?: string | null;
  cart_total_products?: number;
  cart_value?: number;
  prod_sequence?: number;
  gate_level: unknown;
  email?: string | null;
  result_id?: string;
};

type ProductAddedDataLayerProps = {
  event: "add_to_cart";
  items: {
    item_id: string;
    item_sku?: string | null;
    item_name: string;
    item_variant_sku?: string | null;
    item_variant_name: string;
    brand_id: string;
    brand_name: string;
    item_category: { level: string; name: string; id: string }[];
    item_availability?: boolean;
    quantity: string;
    price: number;
    retail_price: number;
    discount_value?: number | null;
    discount_type: string;
    item_custom_labels: { label_id: string; label_name: string }[];
    email?: string | null;
  }[];
};

export type ProductAddedEvent = TrackingEvent<
  "Product Added",
  ProductAddedAnalyticsProps,
  ProductAddedDataLayerProps
>;

export async function trackProductAddedEvent({
  product,
  size,
  variationId,
  cartData,
  lineItems,
  email,
  resultId,
}: ProductAddedEventProps): TrackingEventResponse {
  const currentVariationId =
    variationId ||
    product?.variations?.find((variation) => variation.size === size)?.id;

  return await trackEvent("Product Added", {
    props: {
      brand: product.brand.name,
      brand_id: product.brand.id,
      category: product?.translated_category_names || [],
      category_id: product?.translated_category_ids || [],
      currency: product.currency.code,
      image_url: product.images?.[0],
      labels: (product.labels || []).map(({ value }) => value),
      name: product.name,
      price: product.price,
      product_discount: product.product_discount,
      product_id: product.id,
      product_kids_gender: product.product_shop_type?.includes("kids")
        ? product.product_kids_gender
        : undefined,
      product_shop_type: product.product_shop_type,
      product_size: size,
      quantity: 1,
      regular_price: product.regular_price,
      sku: product.sku,
      url: `product/${product.slug}`,
      variant: size,
      product_variation: variationId,
      gender: product.gender,
      cart_total_products: cartData?.product_count,
      cart_value: cartData?.cart_total,
      prod_sequence: getCartItemsCount(lineItems),
      gate_level: product.gate?.level || null,
      email,
      result_id: resultId,
    },
    dataLayerProps: {
      event: "add_to_cart",
      items: [
        {
          item_id: product.id,
          item_sku: currentVariationId,
          item_name: product.name,
          item_variant_sku: currentVariationId,
          item_variant_name: size,
          brand_id: product.brand.id,
          brand_name: product.brand.name,
          item_category: product?.translated_category_ids?.length
            ? product.translated_category_ids.map((id, index) => ({
                level: (index + 1).toString(),
                name: product?.translated_category_names?.length
                  ? product?.translated_category_names[index] || ""
                  : "",
                id: id || "",
              }))
            : [],
          item_availability: product.variations?.some(
            (item) => item.stock_quantity > 1
          ),
          quantity: "1",
          price: product.price,
          retail_price: product.regular_price,
          discount_value: product.product_discount,
          discount_type: "absolute",
          item_custom_labels: (product.labels || []).map(({ type, value }) => ({
            label_id: type,
            label_name: value,
          })),
          email,
        },
      ],
    },
    withRequestSource: true,
  });
}
